import React from 'react'

import createAsyncData from 'modules/core/hooks/createAsyncData'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'

import type { PolicyPeriods } from 'modules/payroll_bundle/store/collections/policyPeriods'
import PolicyPeriod from 'modules/payroll_bundle/store/models/PolicyPeriod'

interface PolicyPeriodsContextType {
  policyPeriodCollection: PolicyPeriods
  loadingPolicyPeriodCollection: boolean
  currentPolicyPeriods: PolicyPeriod[]
  policyId: number | null
  setCurrentPolicy: (id: number, periodId?: number) => void
  periodIndex: number
  setPeriodIndex: (index: number) => void
  setPeriodId: (id: number) => void
  currentPeriodStatus: string | undefined
  createNextPeriodForPolicy: (policyId: number) => Promise<void>
  createPreviousPeriodForPolicy: (policyId: number) => Promise<void>
  fetchCurrentPolicyPeriod: () => Promise<void>
  currentPeriod?: PolicyPeriod
  prevPeriod?: PolicyPeriod
}

interface PolicyAndPeriods {
  policyId: number | null
  periods: PolicyPeriod[]
  periodIndex: number
}

const useFetch = createAsyncData((policyPeriodCollection, isBookkeeper) => {
  return policyPeriodCollection.fetch({
    data: { bookkeeper_view: isBookkeeper },
  })
})

const Context = React.createContext<PolicyPeriodsContextType | null | undefined>(null)

/**
 * A provider for policy periods.
 *
 * Get the provided value with `usePolicyPeriods`.
 */
export const PolicyPeriodsProvider = ({
  policyPeriodCollection,
  isBookkeeper = false,
  children,
}: {
  policyPeriodCollection: PolicyPeriods
  isBookkeeper?: boolean
  children: React.ReactNode
}) => {
  const loadingPolicyPeriodCollection = useFetch(policyPeriodCollection, isBookkeeper)
  const statuses: Record<string, string> = PolicyPeriod.Statuses()

  const [currentPolicyAndPeriods, setCurrentPolicyAndPeriods] = React.useState<PolicyAndPeriods>({
    policyId: null,
    periods: [],
    periodIndex: 0,
  })

  const setCurrentPolicy = (id: number, periodId?: number) => {
    if (loadingPolicyPeriodCollection) {
      throw new Error(
        'setCurrentPolicy called when policy periods are not yet loaded (loadingPolicyPeriodCollection)'
      )
    }

    // Note that filter returns an array of PolicyPeriod, not a collection
    const periods = policyPeriodCollection.filter({ policy_id: id })

    // Invalid policy ID
    if (!periods.length) {
      setCurrentPolicyAndPeriods((prev) => ({
        ...prev,
        policyId: null,
        periodIndex: 0,
      }))

      return
    }

    let periodIndex = periodId !== undefined ? periods.findIndex((p) => p.id == periodId) : -1
    if (periodIndex === -1) {
      periodIndex = PolicyPeriod.findCurrentOrLatestPeriodIndex(periods)
    }

    setCurrentPolicyAndPeriods({
      policyId: id,
      periods,
      periodIndex,
    })
  }

  const setPeriodIndex = (index: number) => {
    setCurrentPolicyAndPeriods((prev) => ({
      ...prev,
      periodIndex: index,
    }))
  }

  const setPeriodId = (id: number) => {
    const index = currentPolicyAndPeriods.periods.findIndex((period) => period.id == id)

    if (index !== -1) {
      setPeriodIndex(index)
    }
  }

  const filterAndSortPeriods = useUnsafeCallback(
    (policyId: number) => {
      return policyPeriodCollection
        .filter({ policy_id: policyId })
        .sort((p1, p2) => (p1.attributes.starts_on > p2.attributes.starts_on ? 1 : -1))
    },
    [policyPeriodCollection]
  )

  // It also updates the state's periods to include the newly created one and
  // the periodIndex (it's increased by 1 so that it corresponds to the new
  // period). Doing both updates in the same "setState" operation prevents
  // data inconsistencies.
  const createNextPeriodForPolicy = useUnsafeCallback(
    (policyId: number) => {
      return policyPeriodCollection.createNextForPolicy(policyId).then(() => {
        setCurrentPolicyAndPeriods((prev) => ({
          ...prev,
          periods: filterAndSortPeriods(policyId),
          periodIndex: currentPolicyAndPeriods.periodIndex + 1,
        }))
      })
    },
    [currentPolicyAndPeriods, policyPeriodCollection, filterAndSortPeriods]
  )
  const createPreviousPeriodForPolicy = (policyId: number) => {
    return policyPeriodCollection.createPreviousForPolicy(policyId).then(() => {
      setCurrentPolicyAndPeriods((prev) => ({
        ...prev,
        periods: filterAndSortPeriods(policyId),
        periodIndex: 0,
      }))
    })
  }
  const fetchCurrentPolicyPeriod = () => {
    if (currentPolicyAndPeriods.policyId) {
      return policyPeriodCollection.fetch({
        data: { policy_id: currentPolicyAndPeriods.policyId },
      })
    }
    return Promise.resolve()
  }

  const periodIndex = currentPolicyAndPeriods.periodIndex
  const currentPeriod = currentPolicyAndPeriods.periods[periodIndex]
  const previousPeriodIndex = periodIndex - 1
  const currentPolicyPeriods = currentPolicyAndPeriods.periods

  const value: PolicyPeriodsContextType = {
    policyPeriodCollection,
    loadingPolicyPeriodCollection,
    currentPolicyPeriods,
    policyId: currentPolicyAndPeriods.policyId,
    setCurrentPolicy,
    periodIndex,
    setPeriodIndex,
    setPeriodId,
    currentPeriodStatus: currentPeriod ? statuses[currentPeriod.attributes.status] : undefined,
    createNextPeriodForPolicy,
    createPreviousPeriodForPolicy,
    fetchCurrentPolicyPeriod,
    currentPeriod: currentPeriod,
    prevPeriod: previousPeriodIndex >= 0 ? currentPolicyPeriods[previousPeriodIndex] : undefined,
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export const usePolicyPeriods = (): PolicyPeriodsContextType => {
  const context = React.useContext(Context)

  if (!context) {
    // FIXME: This is a temporary fix to avoid breaking the app when the hook is used outside of the provider, it should be removed once the context is always provided
    // throw new Error('usePolicyPeriods hook must be used within a PolicyPeriodsProvider provider')
    return {} as PolicyPeriodsContextType
  }

  return context
}
